// ContactTemplate — for "how to reach us" scenes.
// Hero-style headline + 2-3 channel rows with icon-glyph + label + value.
// Each row is tappable (mailto:, https://, or triggers BookingTemplate).

import { motion } from 'framer-motion';
import { Stage } from '../stage/Stage';
import { Orb } from '../stage/Orb';
import { Eyebrow } from '../stage/Eyebrow';
import { fadeUp, stagger } from '../motion/variants';
import type { SceneContent } from '../types';

interface ContactProps {
  content: SceneContent;
}

export function ContactTemplate({ content }: ContactProps) {
  const { headline, body, eyebrow, items = [] } = content;
  // Each item: { headline (label), body (value/glyph), eyebrow (channel kind) }

  return (
    <Stage>
      {/* Orb anchor — top-center, 64px (warm, present) */}
      <div className="absolute top-16 left-1/2 -translate-x-1/2 max-md:top-8">
        <Orb size={64} />
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        exit="exit"
        variants={stagger(0.12)}
        className="m-auto w-full max-w-[960px] flex flex-col items-center text-center gap-12 mt-24 max-md:mt-16"
      >
        {eyebrow && (
          <motion.div variants={fadeUp}>
            <Eyebrow>{eyebrow}</Eyebrow>
          </motion.div>
        )}
        {headline && (
          <motion.h2 variants={fadeUp} className="text-display-2 font-display text-fg max-w-[20ch] text-balance">
            {headline}
          </motion.h2>
        )}
        {body && (
          <motion.p variants={fadeUp} className="text-subhead text-fg-soft max-w-[52ch]">
            {body}
          </motion.p>
        )}

        <motion.ul variants={stagger(0.1)} className="flex flex-col gap-3 w-full max-w-[640px] mt-8">
          {items.map((it, i) => (
            <motion.li key={i} variants={fadeUp}>
              <a
                href={it.body || '#'}
                target={it.body?.startsWith('http') ? '_blank' : undefined}
                rel="noopener noreferrer"
                className="group flex items-center justify-between gap-6 px-6 py-5 rounded-2xl border border-fg-faint/50 bg-void-800/60 hover:bg-void-700/80 hover:border-accent/40 transition-colors backdrop-blur-sm"
              >
                <div className="flex flex-col gap-1 text-left">
                  {it.eyebrow && (
                    <span className="text-eyebrow text-fg-mute uppercase font-medium">{it.eyebrow}</span>
                  )}
                  <span className="text-headline font-display text-fg">{it.headline}</span>
                </div>
                <span className="text-subhead text-fg-mute font-mono group-hover:text-accent transition-colors">→</span>
              </a>
            </motion.li>
          ))}
        </motion.ul>
      </motion.div>
    </Stage>
  );
}
